// ResultsPage — Step 5 of the wizard.
// Runs the full sizing pipeline and displays BOM, Compute, Storage, Network, and Diagrams tabs.

import { useEffect, useState } from 'react';
import type { Project, SizingResult } from '../types';
import { useProjectStore } from '../store/projectStore';
import { runSizing } from '../lib/calculations';
import { BOMTable } from '../components/results/BOMTable';
import { ComputeSummary } from '../components/results/ComputeSummary';
import { StorageSummary } from '../components/results/StorageSummary';
import { NetworkSummary } from '../components/results/NetworkSummary';
import { DiagramViewer } from '../components/results/DiagramViewer';
import { exportToExcel } from '../lib/export/excel';
import { exportToPdf } from '../lib/export/pdf';

type ResultTab = 'bom' | 'compute' | 'storage' | 'network' | 'diagrams';

export function ResultsPage() {
  const project = useProjectStore(st => st.project);

  if (!project) {
    return (
      <p style={{ color: '#64748b', fontStyle: 'italic', fontSize: 14 }}>
        No project loaded — return to Step 1 to create or open a project.
      </p>
    );
  }

  return <ResultsContent project={project} />;
}

export function ResultsContent({ project }: { project: Project }) {
  const [activeTab, setActiveTab] = useState<ResultTab>('bom');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sizingState, setSizingState] = useState<{
    data: SizingResult | null;
    error: string | null;
    loading: boolean;
  }>({ data: null, error: null, loading: true });

  const activeScenario = project.scenarios.find(s => s.id === project.activeScenarioId)
    ?? project.scenarios.find(s => s.isBaseline)
    ?? project.scenarios[0];

  useEffect(() => {
    setSizingState({ data: null, error: null, loading: true });
    const timer = setTimeout(() => {
      try {
        const data = runSizing(project, activeScenario.id);
        setSizingState({ data, error: null, loading: false });
      } catch (err) {
        setSizingState({ data: null, error: err instanceof Error ? err.message : String(err), loading: false });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [project, activeScenario.id]);

  function handleExportExcel(sizingResult: SizingResult) {
    setExportingExcel(true);
    setTimeout(() => {
      try {
        exportToExcel(project, sizingResult, activeScenario.name);
      } finally {
        setExportingExcel(false);
      }
    }, 0);
  }

  function handleExportPdf(sizingResult: SizingResult) {
    setExportingPdf(true);
    setTimeout(() => {
      try {
        exportToPdf(project, sizingResult, activeScenario.name);
      } finally {
        setExportingPdf(false);
      }
    }, 0);
  }

  if (sizingState.loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '40px 0',
        color: '#64748b',
        fontSize: 14,
      }}>
        <span style={{
          width: 18,
          height: 18,
          border: '2px solid rgba(76,92,104,0.35)',
          borderTopColor: '#CEECF8',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
          display: 'inline-block',
          flexShrink: 0,
        }} />
        Calculating sizing results…
      </div>
    );
  }

  if (sizingState.error) {
    return (
      <div style={{
        padding: 24,
        borderRadius: 8,
        background: '#fef2f2',
        border: '1px solid #fca5a5',
        color: '#b91c1c',
        fontSize: 14,
      }}>
        <strong>Sizing error:</strong> {sizingState.error}
      </div>
    );
  }

  const sizingResult = sizingState.data!;

  const TABS: { key: ResultTab; label: string; badge?: number }[] = [
    { key: 'bom',      label: 'Bill of Materials', badge: sizingResult.bom.length },
    { key: 'compute',  label: 'Compute',            badge: sizingResult.compute.length },
    { key: 'storage',  label: 'Storage',            badge: sizingResult.storage.length },
    { key: 'network',  label: 'Network',            badge: sizingResult.network.length },
    { key: 'diagrams', label: 'Diagrams' },
  ];

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Sizing Results
          </h2>
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {project.name} · {project.clientName}
            </span>
            <span style={{
              padding: '2px 8px',
              borderRadius: 9999,
              background: '#dbeafe',
              color: '#1e40af',
              fontSize: 12,
              fontWeight: 600,
            }}>
              {activeScenario.name}
            </span>
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>
            Generated: {new Date(sizingResult.generatedAt).toLocaleString()}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Scenario switcher hint */}
          {project.scenarios.length > 1 && (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {project.scenarios.length} scenarios — use scenario switcher in top bar
            </span>
          )}

          {/* Excel export */}
          <button
            onClick={() => handleExportExcel(sizingResult)}
            disabled={exportingExcel}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #16a34a',
              background: '#f0fdf4',
              color: '#15803d',
              fontSize: 13,
              fontWeight: 600,
              cursor: exportingExcel ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: exportingExcel ? 0.7 : 1,
            }}
          >
            {exportingExcel ? (
              <>
                <span style={{ width: 12, height: 12, border: '2px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                Exporting…
              </>
            ) : '↓ Export Excel'}
          </button>

          {/* PDF export */}
          <button
            onClick={() => handleExportPdf(sizingResult)}
            disabled={exportingPdf}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #1e40af',
              background: '#eff6ff',
              color: '#1e40af',
              fontSize: 13,
              fontWeight: 600,
              cursor: exportingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: exportingPdf ? 0.7 : 1,
            }}
          >
            {exportingPdf ? (
              <>
                <span style={{ width: 12, height: 12, border: '2px solid #1e40af', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                Exporting…
              </>
            ) : '↓ Export PDF'}
          </button>
        </div>
      </div>

      {/* Key headline metrics */}
      <HeadlineMetrics sizingResult={sizingResult} />

      {/* Tab strip */}
      <div style={{
        display: 'flex',
        gap: 0,
        borderBottom: '2px solid #e2e8f0',
        marginBottom: 24,
        marginTop: 28,
      }}>
        {TABS.map(tab => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderBottom: isActive ? '2px solid #1e40af' : '2px solid transparent',
                marginBottom: -2,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#1e40af' : '#475569',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {tab.label}
              {tab.badge !== undefined && (
                <span style={{
                  padding: '1px 7px',
                  borderRadius: 9999,
                  background: isActive ? '#1e40af' : '#e2e8f0',
                  color: isActive ? '#fff' : '#64748b',
                  fontSize: 11,
                  fontWeight: 700,
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'bom'      && <BOMTable bom={sizingResult.bom} />}
      {activeTab === 'compute'  && <ComputeSummary compute={sizingResult.compute} />}
      {activeTab === 'storage'  && <StorageSummary storage={sizingResult.storage} />}
      {activeTab === 'network'  && <NetworkSummary network={sizingResult.network} />}
      {activeTab === 'diagrams' && <DiagramViewer />}
    </div>
  );
}

// ─── Headline Metrics ─────────────────────────────────────────────────────────

function HeadlineMetrics({ sizingResult }: { sizingResult: SizingResult }) {
  const primaryCompute = sizingResult.compute.filter(r => r.site === 'primary');
  const drCompute = sizingResult.compute.filter(r => r.site === 'dr');
  const primaryStorage = sizingResult.storage.find(r => r.site === 'primary');

  const totalPrimaryHosts = primaryCompute.reduce((s, r) => s + r.hostCountWithHA, 0);
  const totalDrHosts = drCompute.reduce((s, r) => s + r.hostCountWithHA, 0);
  const totalPrimaryRawSan = Math.ceil(primaryStorage?.rawSanGb ?? 0);
  const primaryNetwork = sizingResult.network.find(r => r.site === 'primary');
  const bomItems = sizingResult.bom.length;

  const metrics = [
    { label: 'Primary Hosts',   value: totalPrimaryHosts.toLocaleString(),           unit: 'hosts',    colour: '#1e40af' },
    { label: 'DR Hosts',        value: totalDrHosts.toLocaleString(),                 unit: 'hosts',    colour: '#9d174d' },
    { label: 'Primary SAN Raw', value: totalPrimaryRawSan.toLocaleString(),           unit: 'GB',       colour: '#065f46' },
    { label: 'Uplink',          value: String(primaryNetwork?.recommendedUplinkGbps ?? '—'), unit: 'Gbps', colour: '#7c2d12' },
    { label: 'BOM Lines',       value: bomItems.toLocaleString(),                     unit: 'items',    colour: '#374151' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
      {metrics.map(m => (
        <div key={m.label} style={{
          padding: '14px 16px',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, letterSpacing: '0.03em' }}>
            {m.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{
              fontSize: 24,
              fontWeight: 700,
              color: m.colour,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}>
              {m.value}
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{m.unit}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
